import { useState } from "react";
import { TableCell, TableRow, Typography, useTheme } from "@mui/material";
import { GetSecuritiesPreviewResponseBody, SecurityPreviewObj } from "../../../../redux-store/types/onBoarding";
import CustomTable from "../../../investorOverview/common/customTable";
import { TableHeaderData } from "../../../investorOverview/types";

const headerData: TableHeaderData[] = [
    {
        id: 'securityName',
        label: 'Security Name',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'familyName',
        label: 'Family Name',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'isExcluded',
        label: 'Is Excluded?',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'exclusionReason',
        label: 'Exclusion Reason',
        align: 'center',
        isSortable: false,
    },
];

function DataTable({ tableBodyData }: { tableBodyData: Array<SecurityPreviewObj> }): JSX.Element {
    const theme = useTheme();

    return (
        <>
            {tableBodyData.map((obj, index) => (
                <TableRow
                    key={`${obj.security}-${index}`}
                    sx={{
                        '& td': {
                            paddingTop: "15px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        '& td:first-of-type': {
                            paddingLeft: "15px",
                        }
                    }}
                >
                    <TableCell align="left">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {obj?.securityName ?? "-"} {obj?.security ? "(" + obj?.security + ")" : ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {obj?.familyName ?? "-"} {obj?.familyId ? "(" + obj?.familyId + ")" : ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {["y"].includes(`${obj?.isExcluded ?? ""}`.toLowerCase()) ? "Yes" : "No"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {["y"].includes(`${obj?.isExcluded ?? ""}`.toLowerCase()) ? (obj?.exclusionReason ?? "-") : "-"}
                        </Typography>
                    </TableCell>
                </TableRow>
            ))}
        </>
    );
}

export default function SecurityPreviewTable({
    securitiesPreviewData
}: {
    securitiesPreviewData: GetSecuritiesPreviewResponseBody | null;
}): JSX.Element {
    const allData = securitiesPreviewData?.data ?? [];
    const totalRows = securitiesPreviewData?.count ?? 0;

    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const paginatedData = allData.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
    );

    const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    return (
        <CustomTable
            isLoading={false}
            tableBody={
                <DataTable tableBodyData={paginatedData} />
            }
            tableData={paginatedData}
            tableHeaderData={headerData}
            tableHeadRowCustomStyles={{
                '& th:first-of-type': {
                    paddingLeft: "20px"
                }
            }}
            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0
                }
            }}
            loadingRowsNum={7}
            tableDataCount={totalRows}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handleRowsPerPageChange={handleRowsPerPageChange}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
        />
    );
}
